import React, { FC, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import ImageCropper from './ImageCropper';
import ImageIcon from '@mui/icons-material/Image';
import { styled } from '@mui/system';

const Input = styled('input')({
  display: 'none',
});

interface SystemLogoDialogProps {
  open: boolean;
  onClose: () => void;
  onCropComplete: (croppedImage: File) => void;
}

const SystemLogoDialog: FC<SystemLogoDialogProps> = ({
  open,
  onClose,
  onCropComplete,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Quando o usuário escolhe um arquivo local
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) {
          setSelectedImage(reader.result as string);
          setUploadError(null);
        }
      };
      reader.onerror = (err) => {
        console.error('Erro ao ler o arquivo:', err);
        setUploadError('Falha ao ler o arquivo.');
      };
      reader.readAsDataURL(file);
    }
  };

  // Cancelar o recorte
  const handleCancelCrop = () => {
    setSelectedImage(null);
    setUploadError(null);
  };

  // Recebemos o `File` recortado do <ImageCropper/>
  const handleCropCompleteInternal = async (croppedFile: File) => {
    try {
      onCropComplete(croppedFile);

      // Fecha a modal após confirmar o corte
      handleCancelCrop();
      onClose();
    } catch (err: any) {
      console.error('Erro ao processar o corte:', err);
      setUploadError('Falha ao processar a imagem. Tente novamente.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Upload da Logo do Sistema</DialogTitle>
      <DialogContent>
        {/* Se não houver imagem selecionada ainda, mostra o botão para escolher */}
        {!selectedImage ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              p: 2,
            }}
          >
            <label htmlFor="upload-logo-button">
              <Input
                accept="image/*"
                id="upload-logo-button"
                type="file"
                onChange={handleFileChange}
              />
              <Button variant="contained" component="span" startIcon={<ImageIcon />}>
                Selecionar Logo
              </Button>
            </label>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Escolha uma imagem para a logo do sistema.
            </Typography>
            {uploadError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {uploadError}
              </Typography>
            )}
          </Box>
        ) : (
          // Caso já tenha imagem selecionada, exibimos o componente de recorte (react-advanced-cropper)
          <ImageCropper
            imageSrc={selectedImage}
            onCropComplete={handleCropCompleteInternal}
            onCancel={handleCancelCrop}
          />
        )}
      </DialogContent>
      <DialogActions>
        {/* Se não tiver nenhuma imagem selecionada para recorte, podemos fechar */}
        {!selectedImage && (
          <Button onClick={onClose}>Cancelar</Button>
        )}
        {/* Se já tiver imagem e estivermos no recorte, oferecemos outro botão "Cancelar" */}
        {selectedImage && (
          <Button onClick={handleCancelCrop}>Cancelar</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SystemLogoDialog;